/**
 * Générateur — Utiliser un tableau de proportionnalité et les notations symboliques
 * Attendu ID : 685 | Niveau : 6ème | Domaine : Proportionnalité (cluster 42)
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - tableau_completer_saisie    : saisie — compléter une case manquante d'un tableau 2×4
 *   - coefficient_tableau_saisie  : saisie — trouver le coefficient du tableau
 *   - fleche_calcul_saisie        : saisie — appliquer une opération ×k ou ÷k
 *   - notations_qcm               : QCM 4 choix — interpréter une notation symbolique
 *   - tableau_proprietes_vf       : V/F 6 lignes — propriétés des tableaux de prop.
 *   - construction_tableau_qcm    : QCM 4 choix — choisir le tableau correct pour une situation
 *
 * Spécificité : 100 % texte. Les tableaux sont rendus via <table> HTML inline.
 *
 * Partition avec attendus voisins :
 *   - 682/683 : définition et reconnaissance.
 *   - 684 : procédures de résolution.
 *   - 686 : échelles.
 *
 * Cible : 20/20 uniques sur 20 itérations, 5 runs consécutifs minimum.
 */

import {
    randomChoice, shuffle, randInt,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 685;

const K_POOL = [1.5, 2, 2.5, 3, 4, 5, 0.8];
const K_POOL_ENTIER = [2, 3, 4, 5];
// Sens inverse (décision utilisateur 2026-08-11) : coefficients dont l'inverse
// s'écrit proprement en décimal (recette gen 683) — 1,5 et 3 sont exclus.
const K_POOL_INVERSIBLE = [2, 2.5, 4, 5, 0.8];
const K_POOL_INVERSIBLE_ENTIER = [2, 4, 5];
const TABLE_CONTEXTS = [
    { headerA: 'Masse (kg)',       headerB: 'Prix (€)',      suffixeA: ' kg',     suffixeB: ' €' },
    { headerA: 'Durée (h)',        headerB: 'Distance (km)', suffixeA: ' h',      suffixeB: ' km' },
    // entier : la 2ᵉ ligne est un comptage — coefficient décimal interdit
    // (revue 2026-08-11, recette gen 683 : « 4,5 pages » dans 7,3 % des tirages).
    { headerA: 'Durée (min)',      headerB: 'Pages',         suffixeA: ' min',    suffixeB: ' pages', entier: true },
    { headerA: 'Durée (min)',      headerB: 'Volume (L)',    suffixeA: ' min',    suffixeB: ' L' },
    { headerA: 'Nombre d\'objets', headerB: 'Prix (€)',      suffixeA: ' objets', suffixeB: ' €' },
];

const TRIPLETS = [
    [2, 3, 4],
    [3, 4, 5],
    [4, 5, 6],
    [2, 4, 5],
    [2, 3, 5],
    [3, 5, 6],
    [4, 6, 8],
    [2, 5, 10],
];

const QUADRUPLETS = [
    [1, 2, 3, 4],
    [2, 3, 4, 5],
    [2, 4, 5, 6],
    [1, 2, 4, 5],
    [2, 3, 5, 6],
    [3, 4, 5, 8],
    [2, 4, 6, 8],
    [1, 3, 5, 7],
];

const L_COMPL  = 'Compléter une case d\'un tableau de proportionnalité (saisie)';
const L_COEF   = 'Coefficient d\'un tableau (saisie)';
const L_FLCH   = 'Appliquer une flèche ×k ou ÷k (saisie)';
const L_NOT    = 'Interpréter les notations symboliques (QCM)';
const L_VF     = 'Propriétés des tableaux de proportionnalité (V/F)';
const L_CONS   = 'Choisir le tableau qui représente la situation (QCM)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_COMPL,
});


const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    const s = Number(n).toFixed(2);
    const trimmed = s.replace(/\.?0+$/, '');
    return trimmed.replace('.', ',');
};

// Valeurs en <td> sur les DEUX lignes (revue 2026-08-11, recette gen 683) :
// la 1ʳᵉ ligne en <th> et la 2ᵉ en <td> rendaient les valeurs désalignées.
const buildTable3 = (headerA, headerB, valuesA, valuesB) => {
    const tdA = valuesA.map(v => `<td>${v}</td>`).join('');
    const tdB = valuesB.map(v => `<td>${v}</td>`).join('');
    return `<table><tbody><tr><th>${headerA}</th>${tdA}</tr>` +
        `<tr><th>${headerB}</th>${tdB}</tr></tbody></table>`;
};

const buildTable4 = buildTable3; // même forme, juste 4 colonnes

// ─── Format 1 — Compléter une case (Saisie) ──────────────────

const genTableauCompleterSaisie = () => {
    const ctx = randomChoice(TABLE_CONTEXTS);
    const k = randomChoice(ctx.entier ? K_POOL_ENTIER : K_POOL);
    const quad = randomChoice(QUADRUPLETS);
    const posMasquee = randInt(0, 3);

    // « ? » en ligne du haut dans ~50 % des tirages (décision utilisateur
    // 2026-08-11) : l'élève doit alors DIVISER par le coefficient — le sens
    // inverse existait en théorie (notations, V/F) mais jamais en calcul.
    const masqueEnHaut = Math.random() < 0.5;
    const valuesA = quad.map((v, i) => (masqueEnHaut && i === posMasquee) ? '?' : fmt(v));
    const valuesB = quad.map((v, i) => (!masqueEnHaut && i === posMasquee) ? '?' : fmt(v * k));
    const table = buildTable4(ctx.headerA, ctx.headerB, valuesA, valuesB);

    const reponse = masqueEnHaut ? fmt(quad[posMasquee]) : fmt(quad[posMasquee] * k);
    const texte = `<p>Le tableau ci-dessous est un tableau de proportionnalité.</p>${table}<p>Quelle est la valeur manquante (la case notée « ? ») ?</p>`;

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_COMPL),
        strict: false,
        suffixe: masqueEnHaut ? ctx.suffixeA : ctx.suffixeB,
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-11) : trouver le
    // coefficient passe par des divisions décimales (7,5 ÷ 3).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'tableau_completer_saisie';
    return payload;
};

// ─── Format 2 — Coefficient du tableau (Saisie) ──────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureCoefTab = null;

const genCoefficientTableauSaisie = () => {
    let payload = construireCoefficientTableauSaisie();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureCoefTab; tries++) {
        payload = construireCoefficientTableauSaisie();
    }
    derniereSignatureCoefTab = payload.texte;
    return payload;
};

const construireCoefficientTableauSaisie = () => {
    const ctx = randomChoice(TABLE_CONTEXTS);
    // Sens inverse dans ~50 % des tirages (décision utilisateur 2026-08-11) :
    // on demande le multiplicateur bas → haut, c'est-à-dire l'inverse de k.
    const sensInverse = Math.random() < 0.5;
    const pool = sensInverse
        ? (ctx.entier ? K_POOL_INVERSIBLE_ENTIER : K_POOL_INVERSIBLE)
        : (ctx.entier ? K_POOL_ENTIER : K_POOL);
    const k = randomChoice(pool);
    const triplet = randomChoice(TRIPLETS);

    const valuesA = triplet.map(v => fmt(v));
    const valuesB = triplet.map(v => fmt(v * k));
    const table = buildTable3(ctx.headerA, ctx.headerB, valuesA, valuesB);

    const reponse = sensInverse ? fmt(1 / k) : fmt(k);
    const question = sensInverse
        ? 'Par quel nombre faut-il multiplier les nombres de la ligne du bas pour retrouver ceux de la ligne du haut ?'
        : 'Quel est le coefficient permettant de passer de la ligne du haut à la ligne du bas ?';
    const texte = `<p>Voici un tableau de proportionnalité.</p>${table}<p>${question}</p>`;

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_COEF),
        strict: false,
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-11) : divisions
    // décimales (7,5 ÷ 3), comme le format jumeau du gen 683.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'coefficient_tableau_saisie';
    return payload;
};

// ─── Format 3 — Flèche calcul (Saisie) ───────────────────────

let derniereSignatureFleche = null;

const construireFlecheCalculSaisie = () => {
    const r = Math.random();
    let texte, reponse, suffixe;

    if (r < 0.5) {
        // V1 — multiplication
        const k = randomChoice([1.5, 2, 2.5, 3, 4, 5]);
        const a = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10, 12]);
        reponse = fmt(a * k);
        texte = `<p>Dans un tableau de proportionnalité, une flèche verticale indique « × ${fmt(k)} » pour passer de la première ligne à la seconde. Si la première ligne vaut ${fmt(a)}, quelle est la valeur correspondante sur la deuxième ligne ?</p>`;
        suffixe = '';
    } else {
        // V2 — division (k | a garanti)
        const k = randomChoice([2, 3, 4, 5]);
        // garantir k | a
        const mult = randomChoice([2, 3, 4, 5, 6, 7, 8, 9]);
        const a = k * mult;
        reponse = fmt(a / k);
        texte = `<p>Dans un tableau de proportionnalité, une flèche verticale indique « ÷ ${k} » pour passer de la première ligne à la seconde. Si la première ligne vaut ${a}, quelle est la valeur correspondante sur la deuxième ligne ?</p>`;
        suffixe = '';
    }

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_FLCH),
        strict: false,
        suffixe,
    });
    payload._meta.generatorKey = 'fleche_calcul_saisie';
    return payload;
};

const genFlecheCalculSaisie = () => {
    let payload = construireFlecheCalculSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureFleche; t++) {
        payload = construireFlecheCalculSaisie();
    }
    derniereSignatureFleche = payload.texte;
    return payload;
};

// ─── Format 4 — Notations (QCM) ──────────────────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard) : V3 et V4
// sont des textes fixes, l'élève retombait 2 fois de suite dessus (15 %).
let derniereSignatureNotations = null;

const genNotationsQcm = () => {
    let payload = construireNotationsQcm();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureNotations; tries++) {
        payload = construireNotationsQcm();
    }
    derniereSignatureNotations = payload.texte;
    return payload;
};

const construireNotationsQcm = () => {
    const r = Math.random();
    let question, bonne, choix;

    if (r < 0.25) {
        // V1 — signification de × k
        const k = randomChoice([2, 3, 4, 5, 1.5, 2.5]);
        question = `Dans un tableau de proportionnalité, une flèche verticale « × ${fmt(k)} » de la ligne du haut vers la ligne du bas signifie :`;
        // Structure parallèle et longueurs encadrées (revue 2026-08-11) : la
        // bonne était strictement la plus longue dans 100 % des tirages.
        bonne = `Pour passer de la ligne du haut à la ligne du bas, on multiplie chaque valeur par ${fmt(k)}.`;
        choix = [
            bonne,
            `Pour passer de la ligne du haut à la ligne du bas, on additionne ${fmt(k)} à chacune des valeurs.`,
            `Pour passer de la ligne du haut à la ligne du bas, on divise chaque valeur par ${fmt(k)}.`,
            `Pour passer de la ligne du haut à la ligne du bas, on soustrait ${fmt(k)} à chaque valeur.`,
        ];
    } else if (r < 0.375) {
        // V2 — flèche inverse (× k donné, on cherche ÷ k)
        const k = randomChoice([2, 3, 4, 5, 1.5, 2.5]);
        question = `Dans un tableau de proportionnalité, la ligne du bas est obtenue à partir de la ligne du haut par « × ${fmt(k)} ». Pour revenir de la ligne du bas à la ligne du haut, la flèche est :`;
        bonne = `÷ ${fmt(k)}`;
        choix = [
            bonne,
            `× ${fmt(k)}`,
            `+ ${fmt(k)}`,
            `− ${fmt(k)}`,
        ];
    } else if (r < 0.5) {
        // V2 bis — miroir (÷ k donné, on cherche × k) — revue 2026-08-11.
        const k = randomChoice([2, 3, 4, 5, 1.5, 2.5]);
        question = `Dans un tableau de proportionnalité, la ligne du bas est obtenue à partir de la ligne du haut par « ÷ ${fmt(k)} ». Pour revenir de la ligne du bas à la ligne du haut, la flèche est :`;
        bonne = `× ${fmt(k)}`;
        choix = [
            bonne,
            `÷ ${fmt(k)}`,
            `+ ${fmt(k)}`,
            `− ${fmt(k)}`,
        ];
    } else if (r < 0.75) {
        // V3 — nature du coefficient
        question = `Dans un tableau de proportionnalité à deux lignes, le coefficient de proportionnalité est :`;
        // Miroir additif de même gabarit que la bonne (revue 2026-08-11) :
        // encadre les longueurs ET piège la confusion proportionnel/additif.
        bonne = 'Le nombre non nul qui multiplie chaque valeur de la première ligne pour obtenir la valeur correspondante de la deuxième.';
        choix = [
            bonne,
            'Le nombre qui s\'ajoute à chacune des valeurs de la première ligne pour obtenir la valeur correspondante de la deuxième ligne.',
            'La différence entre deux valeurs correspondantes des deux lignes.',
            'La moyenne des valeurs de la deuxième ligne.',
        ];
    } else {
        // V4 — définition tableau de prop
        question = `Un tableau est un tableau de proportionnalité lorsque :`;
        // Miroir additif de même gabarit que la bonne (revue 2026-08-11).
        bonne = 'Tous les rapports entre les valeurs correspondantes des deux lignes sont égaux à un même nombre non nul.';
        choix = [
            bonne,
            'Les différences entre les valeurs correspondantes des deux lignes sont toutes égales à un même nombre non nul.',
            'Les deux lignes augmentent en même temps.',
            'Chaque colonne du tableau contient des nombres entiers.',
        ];
    }

    const texte = `<p>${question}</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_NOT));
    payload._meta.generatorKey = 'notations_qcm';
    return payload;
};

// ─── Format 5 — Propriétés (V/F) ─────────────────────────────

// Chaque item porte sa clé sémantique `k` (revue 2026-08-11) : une clé ne
// sort qu'une fois par tirage, camps Vrai et Faux confondus — sinon une
// affirmation donne l'autre par simple contradiction (81 % des tirages).
const BUILDERS_V_685 = [
    { k: 'coeff_constant', b: () => 'Dans un tableau de proportionnalité, le coefficient de proportionnalité est le même pour toutes les colonnes.' },
    { k: 'coeff_valeur',   b: ({ k }) => `Dans un tableau de proportionnalité où la ligne du haut est (2 ; 3 ; 4) et la ligne du bas (${fmt(2 * k)} ; ${fmt(3 * k)} ; ${fmt(4 * k)}), le coefficient de proportionnalité vaut ${fmt(k)}.` },
    { k: 'completer',      b: () => 'On peut compléter un tableau de proportionnalité en utilisant le coefficient ou la propriété de linéarité additive.' },
    { k: 'fleche_sens',    b: () => 'Une flèche « × k » d\'une ligne à l\'autre indique qu\'on multiplie par k pour passer de la première à la seconde.' },
    { k: 'inverse',        b: () => 'Si on passe de la ligne du haut à la ligne du bas par « × k », alors on passe de la ligne du bas à la ligne du haut par « ÷ k ».' },
    { k: 'coeff_constant', b: () => 'Dans un tableau de proportionnalité, le rapport entre deux valeurs correspondantes est le même, quelle que soit la colonne.' },
    { k: 'coeff_decimal',  b: () => 'Un tableau de proportionnalité peut avoir un coefficient décimal, par exemple 1,5 ou 0,8.' },
    { k: 'verif',          b: () => 'Pour vérifier qu\'un tableau est de proportionnalité, il faut que tous les rapports colonne par colonne soient égaux.' },
];

const BUILDERS_F_685 = [
    { k: 'coeff_constant', b: () => 'Dans un tableau de proportionnalité, le coefficient change d\'une colonne à l\'autre.' },
    { k: 'fleche_sens',    b: () => 'Une flèche « × k » et une flèche « + k » veulent dire la même chose.' },
    { k: 'verif',          b: () => 'Pour vérifier si un tableau est proportionnel, il suffit de contrôler la première colonne.' },
    { k: 'additif',        b: ({ c }) => `Un tableau où la deuxième ligne est toujours égale à la première ligne plus ${c} est un tableau de proportionnalité.` },
    { k: 'inverse',        b: ({ k }) => `Dans un tableau de proportionnalité de coefficient ${fmt(k)}, pour passer de la ligne du bas à la ligne du haut, on multiplie par ${fmt(k)}.` },
    { k: 'coeff_nul',      b: () => 'Le coefficient de proportionnalité peut être nul.' },
    { k: 'coeff_decimal',  b: () => 'Un tableau de proportionnalité doit toujours contenir uniquement des valeurs entières.' },
    { k: 'coeff_constant', b: () => 'Dans un tableau de proportionnalité, on peut multiplier une seule colonne par un nombre différent des autres.' },
];

const genTableauProprietesVF = () => {
    const texte = `<p>Pour chaque affirmation sur les tableaux de proportionnalité, indique si elle est vraie ou fausse.</p>`;

    for (let attempt = 0; attempt < 8; attempt++) {
        const k = randomChoice([2, 3, 4, 5, 1.5, 2.5]);
        const c = randInt(2, 5);
        const ctxV = { k };
        const ctxF = { k, c };

        const seenKeys = new Set();
        const pick = (pool, n) => {
            const res = [];
            for (const item of shuffle([...pool])) {
                if (res.length === n) break;
                if (seenKeys.has(item.k)) continue;
                seenKeys.add(item.k);
                res.push(item);
            }
            return res;
        };
        // Quota variable (revue 2026-08-11) : 3V/3F constant était comptable.
        const nbV = randInt(2, 4);
        const lignesV = pick(BUILDERS_V_685, nbV).map(item => item.b(ctxV));
        const lignesF = pick(BUILDERS_F_685, 6 - nbV).map(item => item.b(ctxF));

        const combined = shuffle([
            ...lignesV.map(l => ({ ligne: l, br: 0 })),
            ...lignesF.map(l => ({ ligne: l, br: 1 })),
        ]);

        const lignes = combined.map(x => x.ligne);
        const bonnesReponses = combined.map(x => x.br);

        if (new Set(lignes).size === lignes.length) {
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'tableau_proprietes_vf';
            return payload;
        }
    }

    // Fallback
    const fallbackLignes = [
        'Dans un tableau de proportionnalité, le coefficient de proportionnalité est le même pour toutes les colonnes.',
        'On peut compléter un tableau de proportionnalité en utilisant le coefficient ou la propriété de linéarité additive.',
        'Un tableau de proportionnalité peut avoir un coefficient décimal, par exemple 1,5 ou 0,8.',
        'Dans un tableau de proportionnalité, le coefficient change d\'une colonne à l\'autre.',
        'Le coefficient de proportionnalité peut être nul.',
        'Une flèche « × k » et une flèche « + k » veulent dire la même chose.',
    ];
    const fallbackBr = [0, 0, 0, 1, 1, 1];
    const order = shuffle([0, 1, 2, 3, 4, 5]);
    const lignes = order.map(i => fallbackLignes[i]);
    const bonnesReponses = order.map(i => fallbackBr[i]);
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'tableau_proprietes_vf';
    return payload;
};

// ─── Format 6 — Construction du tableau (QCM) ────────────────

const genConstructionTableauQcm = () => {
    const ctx = randomChoice(TABLE_CONTEXTS);
    const k = randomChoice([2, 3, 4, 5]);
    // [1, 2, 3] exclu (revue 2026-08-11) : le distracteur quadratique
    // affichait une colonne « 1 -> 1 », éliminable sans calcul.
    const triplet = randomChoice([[3, 5, 6], [2, 3, 4], [3, 4, 5], [2, 4, 6]]);

    const valuesA = triplet.map(v => fmt(v));

    // Bon tableau : ligne 2 = k × ligne 1
    const correct = triplet.map(v => fmt(v * k));
    // Faux 1 : additif (ligne 2 = ligne 1 + c)
    const cAdd = randomChoice([2, 3, 5]);
    const additif = triplet.map(v => fmt(v + cAdd));
    // Faux 2 : quadratique (ligne 2 = ligne 1²)
    const quadratique = triplet.map(v => fmt(v * v));
    // Faux 3 : incohérent — 2 colonnes ok, 1 colonne fausse
    const posErr = randInt(0, 2);
    const incoherent = triplet.map((v, i) => fmt(i === posErr ? v * k + 2 : v * k));

    const tables = [
        { label: 'Tableau A', values: correct, isCorrect: true },
        { label: 'Tableau B', values: additif, isCorrect: false },
        { label: 'Tableau C', values: quadratique, isCorrect: false },
        { label: 'Tableau D', values: incoherent, isCorrect: false },
    ];

    const shuffled = shuffle(tables);
    // Réassigner les labels selon l'ordre mélangé
    const labels = ['Tableau A', 'Tableau B', 'Tableau C', 'Tableau D'];
    const relabeled = shuffled.map((t, i) => ({ ...t, label: labels[i] }));

    const tablesHtml = relabeled.map(t => {
        const tbl = buildTable3(ctx.headerA, ctx.headerB, valuesA, t.values);
        return `<p><strong>${t.label}</strong></p>${tbl}`;
    }).join('');

    const bonne = relabeled.find(t => t.isCorrect).label;
    const choix = labels;

    // Énoncé allégé (revue 2026-08-11) : une seule mention du coefficient.
    const texte = `<p>Deux grandeurs sont proportionnelles : on passe de la ligne du haut à la ligne du bas en multipliant par ${k}. Quel tableau correspond à cette situation ?</p>${tablesHtml}`;

    const payload = makeQcm(choix, bonne, texte, opts(L_CONS));
    payload._meta.generatorKey = 'construction_tableau_qcm';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    tableau_completer_saisie:    genTableauCompleterSaisie,
    coefficient_tableau_saisie:  genCoefficientTableauSaisie,
    fleche_calcul_saisie:        genFlecheCalculSaisie,
    notations_qcm:               genNotationsQcm,
    tableau_proprietes_vf:       genTableauProprietesVF,
    construction_tableau_qcm:    genConstructionTableauQcm,
};

export const CATEGORIES = [
    {
        id: 'proportionnalite_tableaux',
        label: 'Tableaux de proportionnalité et notations symboliques',
        formatsList: [
            { key: 'tableau_completer_saisie',   label: L_COMPL },
            { key: 'coefficient_tableau_saisie', label: L_COEF },
            { key: 'fleche_calcul_saisie',       label: L_FLCH },
            { key: 'notations_qcm',              label: L_NOT },
            { key: 'tableau_proprietes_vf',      label: L_VF },
            { key: 'construction_tableau_qcm',   label: L_CONS },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    K_POOL,
    TABLE_CONTEXTS,
    TRIPLETS,
    QUADRUPLETS,
    BUILDERS_V_685,
    BUILDERS_F_685,
    fmt,
    buildTable3,
    buildTable4,
    genTableauCompleterSaisie,
    genCoefficientTableauSaisie,
    genFlecheCalculSaisie,
    genNotationsQcm,
    genTableauProprietesVF,
    genConstructionTableauQcm,
};
